import { CustomerProjectTypeModelType } from '@teinor/erp/types/company/customer/customerProjectType'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderBoolean, CustomRenderColor, CustomRenderString, DeleteRow, EditRowNotLink } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import ModalCECustomerProjectType from './ModalCECustomerProjectType'
import { ConnectedProps, connect } from 'react-redux'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { TaskTypePayload } from '../../../../../../utils/reducers/company/taskTypes'
import { useParams } from 'react-router-dom'
import DoubleTable from '../../../../../../baseComponents/DoubleTable'
import { useAddDoubleTableGreenRowRender } from '../../../../../../baseComponents/DoubleTable/functions'
import CustomerProjectStatus from './CustomerProjectStatus'
import ModalDeleteCustomerProjectType from './ModalDeleteCustomerProjectType'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import locations from '../../../../../../utils/globals/locations'
import { CustomerProjectTypePayload } from '../../../../../../utils/reducers/customers/customerProjectTypes'

interface CustomerProjectTypesProps extends ComponentWithPermissions {}

const CustomerProjectTypes: React.FC<CustomerProjectTypesProps> = ({ userPermissions }) => {
    const { CustomerProjectTypeId } = useParams()
    return (<DoubleTable userPermissions={userPermissions} FirstChild={CustomerProjectTypesInner} SecondChild={CustomerProjectStatus} displaySecond={CustomerProjectTypeId !== 'main'} />)
}
const CustomerProjectTypesInner: React.FC<CustomerProjectTypesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('customerProjectTypes', 'Tipos de proyecto'),
        newText: t('newCustomerProjectType', 'Nuevo tipo de proyecto'),
    }

    return (
        <BaseScreenTable TableComponent={TableCustomerProjectTypesConnect} CreateModalComponent={ModalCECustomerProjectType} EditModalComponent={ModalCECustomerProjectType} DeleteModalComponent={ModalDeleteCustomerProjectType} pageData={pageData} userPermissions={userPermissions} />
    )
}

interface TableCustomerProjectTypesProps extends TableComponentProps{}

const TableCustomerProjectTypes: React.FC<TableCustomerProjectTypesProps & ReduxTaskTypes> = ({ setTableInstance, setOpened, taskTypes, userPermissions }) => {
    const { t } = useTwinTranslation()
    const { CustomerProjectTypeId } = useParams()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(CustomerProjectTypeId || '')
    const columns: ColumnTableSchema<CustomerProjectTypeModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 80
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            minWidth: 130,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'color',
            dataKey: 'color',
            label: t('color', 'Color'),
            customRender: (parameterValue) => <CustomRenderColor value={parameterValue} />
        },
        {
            id: 'taskTypeId',
            dataKey: 'TaskTypeId',
            label: t('tastType', 'Tipo de tarea'),
            customRender: (parameterValue) => <CustomRenderString value={taskTypes?.[parameterValue as any]?.name || ''}/>
        },
        {
            id: 'is_project_main',
            dataKey: 'is_project_main',
            label: t('projectMainS', 'Principal proy.'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={56} />,
            generalSearchable: false
        },
        {
            id: 'is_opportunity_main',
            dataKey: 'is_opportunity_main',
            label: t('OppMainS', 'Principal oport.'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={56} />,
            generalSearchable: false
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabledS', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRowNotLink key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />
        },
         {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]

    return (
        <TwinTableLink createRowDataLink={[locations.customerProjectTypes, ['$id']]} name='CustomerProjectTypes' columns={columns} getDataFrom='/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypes' setTableInstance={setTableInstance} rowRenderer={rowRenderer} hideLoadFiltersButton={true} />)
}

const taskTypesDispatch = {
    setTaskTypes: (payload: TaskTypePayload) => ({ type: 'CHANGE_TASKTYPE', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
}

export type ReduxTaskTypes = ConnectedProps<typeof taskTypesConnect>
const mapTaskTypesConnector = (state: AllReduxPayloads) => ({ taskTypes: state.taskTypes, customerProjectTypes: state.customerProjectTypes })
const taskTypesConnect = connect(mapTaskTypesConnector, taskTypesDispatch)
const taskTypesConnectLoading = withLoading(TableCustomerProjectTypes, [{ fetchUrl: '/api/app/task/taskType/getAllTaskTypes', propName: 'taskTypes', setFunctionName: 'setTaskTypes' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }])
const TableCustomerProjectTypesConnect = taskTypesConnect(taskTypesConnectLoading)



export default CustomerProjectTypes